import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import { leanPMS, restManager, util } from 'app/ajs-upgraded-providers';

import { ConfigurationManagerService, ConfigurationPreferences, EntityManagerService, ErrorsLoggerService, UserActiveSessionManagerService } from 'src/app/core';
import { AlertDialogService, AutomaticPrintSettingsDialogService, ConfirmDialogService, ItemListSelectorDialogService } from 'src/app/dialogs';
import { SignupFormDialogService } from 'src/app/dialogs/signup-form-dialog/signup-form-dialog.component';
import { EInvoiceCredentialsDialogService } from 'src/app/dialogs/e-invoice-credentials-dialog';
import { EInvoiceSetupDialogService } from 'src/app/dialogs/e-invoice-setup-dialog/e-invoice-setup-dialog.component';
import { SaleBatchPrintService } from 'src/app/features/cashregister/services/sale-batch-print.service';
import { LeanPMSApiService } from 'src/app/features/digital-payments';

import { EnvironmentConfig } from 'src/environments/environment-config';

import { LogoEditorDialogService } from '../dialogs/logo-editor/logo-editor-dialog.component';
import { SIGNUP_FORM_FIELDS } from '../registry/signup-form-fields';

import type {
    SettingRecord,
    SettingsActionId,
    SettingsActionResult,
    SettingsContext,
    SettingsDeviceSettingId,
    SettingsLookupId
} from '../models/settings-hub.model';

const TOAST_DURATION = 10000;

/**
 * Runs the actions of the registry (buttons that do something instead of editing a preference: dialogs, requests to
 * external services...). Each handler may return values to apply to the page, see `SettingsActionResult`.
 */
@Injectable()
export class SettingsHubActionsService {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly automaticPrintSettingsDialogService = inject(AutomaticPrintSettingsDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly eInvoiceCredentialsDialogService = inject(EInvoiceCredentialsDialogService);
    private readonly eInvoiceSetupDialogService = inject(EInvoiceSetupDialogService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly errorsLoggerService = inject(ErrorsLoggerService);
    private readonly itemListSelectorDialogService = inject(ItemListSelectorDialogService);
    private readonly leanPMSApiService = inject(LeanPMSApiService);
    private readonly leanPMSService = inject(leanPMS);
    private readonly logoEditorDialogService = inject(LogoEditorDialogService);
    private readonly restManagerService = inject(restManager);
    private readonly saleBatchPrintService = inject(SaleBatchPrintService);
    private readonly signupFormDialogService = inject(SignupFormDialogService);
    private readonly snackBar = inject(MatSnackBar);
    private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
    private readonly utilService = inject(util);
    private readonly translateService = inject(TranslateService);

    private readonly handlers: Record<SettingsActionId, (context: SettingsContext) => Promise<SettingsActionResult>> = {
        'refresh-items-cache': () => this.sendRequest('items', 'refresh_cache', 'SETTINGS.SETTINGS.CACHE_GENERATION_REQUEST_SENT', 'SETTINGS.SETTINGS.CACHE_GENERATION_REQUEST_ERROR'),
        'force-four-dem-sync': () => this.sendRequest('customers', 'sync_4dem', 'SETTINGS.SETTINGS.SYNC_REQUEST_SENT', 'SETTINGS.SETTINGS.SYNC_REQUEST_ERROR'),
        'open-einvoice-setup': async () => { await this.eInvoiceSetupDialogService.openDialog(); },
        'search-vat-merchant': context => this.searchVatMerchant(context),
        'reattach-lean-webhook': context => this.reattachLeanWebhook(context),
        'disconnect-lean': () => this.disconnectLean(),
        'disconnect-mago-orders': () => this.disconnectMagoOrders(),
        'activate-pienissimo-sync': context => this.activatePienissimoSync(context),
        'deactivate-pienissimo-sync': () => this.deactivatePienissimoSync(),
        'sync-pienissimo-products': () => this.requestPienissimoSync('pienissimo/sync', 'SETTINGS.SETTINGS.PIENISSIMO_SYNC_PRODUCTS_ACTIVATED'),
        'sync-pienissimo-tables': () => this.requestPienissimoSync('pienissimo/sync_tables', 'SETTINGS.SETTINGS.PIENISSIMO_SYNC_TABLES_ACTIVATED'),
        'open-digitalhub-credentials': async () => { await this.eInvoiceCredentialsDialogService.openDialog(); },
        'init-retailforce-auto-close': () => this.initRetailForceAutoClose(),
        'add-signup-form-field': context => this.addSignupFormField(context),
        'open-signup-form-preview': async () => this.openSignupFormPreview(),
        'open-printer-logo-editor': async () => { await this.logoEditorDialogService.openDialog(); }
    };

    /** Runs an action; a failure that the handler does not handle (e.g. no connection) is logged and shown to the user */
    async run(actionId: SettingsActionId, context: SettingsContext) {
        try {
            return await this.handlers[actionId](context);
        } catch (error) {
            this.errorsLoggerService.err(error);
            this.alert('SETTINGS.SETTINGS.SAVE_EDIT_ERROR');
        }
    }

    /** The value of a setting of the device: it is read from a signal, so the page also shows the changes made elsewhere (e.g. from the sidenav) */
    deviceSetting(deviceSettingId: SettingsDeviceSettingId): boolean {
        return deviceSettingId === 'print-external-orders' ? this.saleBatchPrintService.externalOrdersPrint() : this.saleBatchPrintService.externalSalesPrint();
    }

    /**
     * Changes a setting of the device, at once. To print the sales automatically a printer and a document are needed:
     * when they are not set, the dialog to choose them opens first, and the setting is turned on only if the user confirms it.
     * If the dialog cannot be opened (e.g. the printers cannot be read) the setting is left as it was and the user is told.
     * The dialog saves the printer and the document by itself: they are given back, so that the page shows them.
     */
    async setDeviceSetting(deviceSettingId: SettingsDeviceSettingId, enabled: boolean): Promise<SettingsActionResult> {
        if (deviceSettingId === 'print-external-orders') {
            this.saleBatchPrintService.setExternalOrdersPrint(enabled);
            return;
        }

        try {
            const alreadyChosen = enabled && this.hasDefaultPrinterAndDocument();

            if (enabled && !alreadyChosen && !(await this.openAutomaticPrintSettingsDialog())) {
                return;
            }

            this.saleBatchPrintService.setExternalSalesPrint(enabled);

            if (enabled && !alreadyChosen) {
                return {
                    values: {
                        'cashregister.batch_sale_print.default_printer_id': this.configurationManagerService.getShopPreference('cashregister.batch_sale_print.default_printer_id'),
                        'cashregister.batch_sale_print.default_document_id': this.configurationManagerService.getShopPreference('cashregister.batch_sale_print.default_document_id')
                    },
                    persisted: true
                };
            }
        } catch (error) {
            this.errorsLoggerService.err(error);
            this.alert('SETTINGS.SETTINGS.SAVE_EDIT_ERROR');
        }
    }

    private hasDefaultPrinterAndDocument() {
        return !!(this.configurationManagerService.getShopPreference('cashregister.batch_sale_print.default_printer_id')
            && this.configurationManagerService.getShopPreference('cashregister.batch_sale_print.default_document_id'));
    }

    private async openAutomaticPrintSettingsDialog() {
        const documentPrinters = await this.entityManagerService.printers.fetchCollectionOffline({ type_in: ['fiscal', 'rt', 'receipt'] });
        const nonFiscalDocuments = await this.entityManagerService.nonfiscalDocuments.fetchCollectionOffline();

        return this.automaticPrintSettingsDialogService.openDialog({ data: { documentPrinters, nonFiscalDocuments } });
    }

    /** Lets the user pick a number from a list; returns undefined if nothing has been picked */
    async lookup(lookupId: SettingsLookupId): Promise<number | undefined> {
        try {
            switch (lookupId) {
                case 'lean-cash-transaction-type':
                    return await this.pickLeanCashTransactionType();
            }
        } catch (error) {
            this.errorsLoggerService.err(error);
            this.alert('SETTINGS.SETTINGS.SAVE_EDIT_ERROR');
        }
    }

    private async sendRequest(resource: string, id: string, successLabel: string, errorLabel: string) {
        let messageLabel = successLabel;

        try {
            await this.restManagerService.getOne(resource, id);
        } catch (error) {
            messageLabel = errorLabel;
        }

        this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant(messageLabel) } });
    }

    /** Fills the data of the e-invoice merchant with the ones found for its VAT code */
    private async searchVatMerchant(context: SettingsContext): Promise<SettingsActionResult> {
        try {
            const result = await this.restManagerService.getOne('validate_vat', context.getPreference('e_invoice.merchant.vat_code'));

            if (!result) {
                return this.alert('SETTINGS.SETTINGS.NO_RESULTS');
            }

            return {
                values: {
                    'e_invoice.merchant.tax_code': result.tax_number?.slice(0, 16) || '',
                    'e_invoice.merchant.company_name': result.name ?? '',
                    'e_invoice.merchant.vat_code': result.vat_number ?? '',
                    'e_invoice.merchant.country': 'IT',
                    'e_invoice.merchant.head_office.street': result.address_street ?? '',
                    'e_invoice.merchant.head_office.number': result.address_number ?? '',
                    'e_invoice.merchant.head_office.zip': result.address_zip ?? '',
                    'e_invoice.merchant.head_office.city': result.address_city ?? '',
                    'e_invoice.merchant.head_office.prov': result.address_prov ?? '',
                    'e_invoice.merchant.head_office.country': 'IT'
                }
            };
        } catch (error) {
            return this.alert('SETTINGS.SETTINGS.NO_INFO_FOUND');
        }
    }

    private async pickLeanCashTransactionType() {
        if (!await this.leanPMSService.isSetup()) {
            return;
        }

        try {
            const types = await this.leanPMSApiService.getCashTransactionsTypes() as unknown as { id: number; name: string }[];

            const result = await this.itemListSelectorDialogService.openDialog({
                data: { message: '', items: types.map(({ id, name }) => ({ id, name: `${id} - ${name}` })), options: { disableSetAsDefault: true } }
            });

            return result?.element?.id;
        } catch (error: any) {
            this.errorsLoggerService.err(error);
        }
    }

    private async reattachLeanWebhook(context: SettingsContext) {
        if (!await this.leanPMSService.isSetup() || context.getSavedPreference('lean_pms.webhooks.hotel_id') === null) {
            this.errorsLoggerService.err('reattachWebhook: not Permission');
            return;
        }

        try {
            const data = await this.leanPMSApiService.setupWebhooks();

            this.toast(data ? 'SETTINGS.LEAN_PMS.REATTACH_WEBHOOK_COMPLETE' : 'SETTINGS.LEAN_PMS.REATTACH_WEBHOOK_FAILED');
        } catch (error: any) {
            this.errorsLoggerService.err(error);
            this.toast(error?.data?.type === 'not_found' ? 'SETTINGS.LEAN_PMS.REATTACH_WEBHOOK_ERROR' : 'SETTINGS.LEAN_PMS.REATTACH_WEBHOOK_FAILED');
        }
    }

    private async disconnectLean() {
        if (!await this.confirm('SETTINGS.LEAN_PMS.DISCONNECT_LEAN_CONFIRMATION')) {
            return;
        }

        try {
            await this.leanPMSApiService.disconnect();
            this.toast('SETTINGS.LEAN_PMS.DISCONNECT_LEAN_COMPLETE');
        } catch (error) {
            this.toast('SETTINGS.LEAN_PMS.DISCONNECT_LEAN_FAILED');
        }
    }

    private async disconnectMagoOrders() {
        if (!await this.confirm('SETTINGS.MAGO.ORDERS_LOGOUT_CONFIRMATION')) {
            return;
        }

        const magoPreferences = Object.keys(this.configurationManagerService.getShopPreferences('integrations.mago.orders')) as (keyof ConfigurationPreferences)[];

        for (const preference of magoPreferences) {
            this.configurationManagerService.deleteShopPreference(preference);
        }

        this.toast('SETTINGS.MAGO.ORDERS_LOGOUT_COMPLETE');
    }

    /** Checks the Pienissimo token with the user, then activates the synchronization */
    private async activatePienissimoSync(context: SettingsContext): Promise<SettingsActionResult> {
        const token = context.getPreference('pienissimo.token');
        const daysToCheck = context.getPreference('pienissimo.days_to_check');

        if (!token || !daysToCheck) {
            return;
        }

        try {
            const result = await this.restManagerService.post('pienissimo/token_check', { token });

            if (!result?.data) {
                return;
            }

            const message: string[] = [];

            if (result.data['partita_iva'] != context.getPreference('general.vat_code')) {
                message.push(this.translateService.instant('SETTINGS.SETTINGS.PIENISSIMO_VAT_MISMATCH'), '');
            }

            for (const [key, value] of Object.entries(result.data)) {
                const label = key.replace(/_/g, ' ');
                message.push(`${label.charAt(0).toUpperCase()}${label.slice(1)}: ${value}`);
            }

            message.push('', this.translateService.instant('SETTINGS.SETTINGS.PIENISSIMO_CONFIRM_SYNC_ACTIVATION'));

            if (!await this.confirm(message.join('\n'))) {
                return;
            }

            await this.restManagerService.post('pienissimo/init', { 'pienissimo.token': token, 'pienissimo.days_to_check': daysToCheck });
            this.toast('SETTINGS.SETTINGS.PIENISSIMO_SYNC_ACTIVATED');

            // The server saved the preferences: the local store is aligned with it
            this.configurationManagerService.setShopPreference('pienissimo.token', token as string);
            this.configurationManagerService.setShopPreference('pienissimo.days_to_check', daysToCheck as number);

            return { values: { 'pienissimo.token': token, 'pienissimo.days_to_check': daysToCheck }, persisted: true };
        } catch (error: any) {
            this.toastMessage(error?.data?.message);
        }
    }

    private async deactivatePienissimoSync(): Promise<SettingsActionResult> {
        await this.configurationManagerService.setShopPreferenceSync('pienissimo.token', '');
        await this.configurationManagerService.setShopPreferenceSync('pienissimo.days_to_check', '' as unknown as number);

        return { values: { 'pienissimo.token': '', 'pienissimo.days_to_check': null }, persisted: true };
    }

    private async requestPienissimoSync(resource: string, successLabel: string) {
        try {
            await this.restManagerService.getOne(resource);
            this.toast(successLabel);
        } catch (error: any) {
            this.toastMessage(error?.data?.message);
        }
    }

    private async initRetailForceAutoClose() {
        try {
            await this.restManagerService.getOne('importing/retail_force');
            this.toast('SETTINGS.RETAILFORCE.INIT_AUTO_CLOSE_SUCCESS');
        } catch (error) {
            this.toast('SETTINGS.RETAILFORCE.INIT_AUTO_CLOSE_FAILED');
        }
    }

    /** Asks which customer fields to add to the public registration form */
    private async addSignupFormField(context: SettingsContext): Promise<SettingsActionResult> {
        const currentFields = Array.isArray(context.getPreference('signup_form.fields')) ? context.getPreference('signup_form.fields') as SettingRecord[] : [];
        const usedNames = new Set(currentFields.map(field => field['name']));

        const result = await this.signupFormDialogService.openDialog({
            data: {
                title: 'DIALOG.SIGNUP_FORM_DIALOG.TITLE',
                signupFormFields: SIGNUP_FORM_FIELDS.filter(field => !usedNames.has(field.name))
            }
        });

        if (!result) {
            return;
        }

        const addedFields = result.signupFormFields.map(({ labelHardcoded, ...field }: SettingRecord) => field);

        return { values: { 'signup_form.fields': [...currentFields, ...addedFields] } };
    }

    private openSignupFormPreview() {
        const shop = this.userActiveSessionManagerService.getSession()?.shop.name;
        const prefix = EnvironmentConfig.environment === 'beta' ? 'https://forms.beta' : 'https://forms';

        this.utilService.openExternalLink(`${prefix}.tilby.com/registration?shop=${shop}`);
    }

    private alert(messageLabel: string) {
        this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant(messageLabel) } });
    }

    private async confirm(messageLabel: string) {
        return !!await this.confirmDialogService.openDialog({ data: { messageLabel } });
    }

    private toast(messageLabel: string) {
        this.toastMessage(this.translateService.instant(messageLabel));
    }

    private toastMessage(message: string | undefined) {
        this.snackBar.open(message ?? '', undefined, { duration: TOAST_DURATION, horizontalPosition: 'start', verticalPosition: 'bottom' });
    }
}
